import './styles/main.css';
import { GameEngine } from './core/GameEngine';
import { GameConfig, SceneType } from './types/game.types';
import { MainMenuScene } from './ui/MainMenuScene';
import { GameSceneManager } from './ui/GameScene';
import { LeaderboardScene } from './ui/LeaderboardScene';

class Application {
  private gameEngine: GameEngine | null = null;
  private canvas: HTMLCanvasElement | null = null;
  private gameSceneManager: GameSceneManager | null = null;
  private currentGameId: string | null = null;
  private orientationOverlay: HTMLElement | null = null;

  public async init(): Promise<void> {
    console.log('Initializing C64 Arcade Application');

    // Set up orientation overlay
    this.orientationOverlay = document.getElementById('orientationOverlay');

    // Set up canvas
    this.setupCanvas();

    // Create game configuration with responsive dimensions
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

    const config: GameConfig = {
      width: 800,
      height: 600,
      fps: 60,
      scale: isMobile ? 1.0 : 0.8 // Full scale on mobile for better visibility
    };

    // Initialize game engine
    if (this.canvas) {
      this.gameEngine = new GameEngine(this.canvas, config);

      // Set up input manager with canvas reference
      this.gameEngine.getInputManager().setCanvas(this.canvas);

      // Expose input manager globally for mobile controls
      (window as any).inputManager = this.gameEngine.getInputManager();

      // Initialize audio (requires user interaction)
      await this.initializeAudio();

      // Register scenes
      this.registerScenes();

      // Start the engine
      this.gameEngine.start();

      // Switch to main menu
      this.gameEngine.switchScene(SceneType.MENU);
    }

    // Set up orientation checking
    this.setupOrientationCheck();

    // Hide loading screen
    this.hideLoadingScreen();

    console.log('C64 Arcade Application initialized');
  }

  private setupCanvas(): void {
    // Remove loading screen content
    const app = document.getElementById('app');
    if (app) {
      app.innerHTML = '';
      
      // Create canvas
      this.canvas = document.createElement('canvas');
      this.canvas.id = 'gameCanvas';
      
      // Create UI container
      const uiContainer = document.createElement('div');
      uiContainer.className = 'ui-container';
      
      // Add to DOM
      app.appendChild(this.canvas);
      app.appendChild(uiContainer);
      
      // Make canvas responsive
      this.makeCanvasResponsive();
      
      // Listen for resize events
      window.addEventListener('resize', () => this.makeCanvasResponsive());
      window.addEventListener('orientationchange', () => {
        // Delay to allow browser to update viewport dimensions
        setTimeout(() => this.makeCanvasResponsive(), 100);
      });
    }
  }
  
  private makeCanvasResponsive(): void {
    if (!this.canvas) return;

    // Check current game for aspect ratio
    // Block Drop uses portrait 600x900, others use landscape 800x600
    const currentGame = (window as any).currentGame;
    const isBlockDrop = currentGame === 'block-drop';

    let baseWidth: number;
    let baseHeight: number;

    if (isBlockDrop) {
      // Portrait for Block Drop
      baseWidth = 600;
      baseHeight = 900;
    } else {
      // Landscape for other games
      baseWidth = 800;
      baseHeight = 600;
    }

    const aspectRatio = baseWidth / baseHeight;

    // Get viewport dimensions - use visualViewport for mobile to account for browser chrome
    const viewportWidth = window.visualViewport?.width || window.innerWidth;
    const viewportHeight = window.visualViewport?.height || window.innerHeight;

    // Detect mobile
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
                     (window.matchMedia && window.matchMedia("(max-width: 768px)").matches);

    let canvasWidth: number;
    let canvasHeight: number;

    if (isMobile) {
      // For mobile, maximize screen usage while maintaining aspect ratio
      // For portrait games (height > width), prioritize filling height
      if (baseHeight > baseWidth) {
        // Portrait game: fill height first
        canvasHeight = viewportHeight;
        canvasWidth = canvasHeight * aspectRatio;

        // If width is too wide, scale down
        if (canvasWidth > viewportWidth) {
          canvasWidth = viewportWidth;
          canvasHeight = canvasWidth / aspectRatio;
        }
      } else {
        // Landscape game: fill width first
        canvasWidth = viewportWidth;
        canvasHeight = canvasWidth / aspectRatio;

        // If height is too tall, scale down
        if (canvasHeight > viewportHeight) {
          canvasHeight = viewportHeight;
          canvasWidth = canvasHeight * aspectRatio;
        }
      }

      console.log(`Mobile canvas sizing: game=${currentGame}, base=${baseWidth}x${baseHeight}, viewport=${viewportWidth}x${viewportHeight}, canvas=${canvasWidth}x${canvasHeight}`);
    } else {
      // Desktop scaling logic
      canvasWidth = viewportWidth * 0.9;
      canvasHeight = canvasWidth / aspectRatio;

      if (canvasHeight > viewportHeight * 0.9) {
        canvasHeight = viewportHeight * 0.9;
        canvasWidth = canvasHeight * aspectRatio;
      }

      // Apply maximum size constraints for desktop
      const maxWidth = 1200;
      if (canvasWidth > maxWidth) {
        canvasWidth = maxWidth;
        canvasHeight = maxWidth / aspectRatio;
      }
    }

    // Set canvas internal resolution
    this.canvas.width = baseWidth;
    this.canvas.height = baseHeight;

    // Set canvas display size
    this.canvas.style.width = `${canvasWidth}px`;
    this.canvas.style.height = `${canvasHeight}px`;

    // Position the canvas
    if (isMobile) {
      // Mobile: align to top-center
      this.canvas.style.position = 'fixed'; // Match CSS
      this.canvas.style.left = '50%';
      this.canvas.style.top = '0';
      this.canvas.style.transform = 'translateX(-50%)';
      this.canvas.style.margin = '0';
      this.canvas.style.border = 'none';
    } else {
      // Desktop: center both directions
      this.canvas.style.position = 'absolute';
      this.canvas.style.left = '50%';
      this.canvas.style.top = '50%';
      this.canvas.style.transform = 'translate(-50%, -50%)';
      this.canvas.style.margin = '0';
    }

    console.log('Canvas styled:', {
      styleWidth: this.canvas.style.width,
      styleHeight: this.canvas.style.height,
      internalWidth: this.canvas.width,
      internalHeight: this.canvas.height
    });

    // Ensure pixel-perfect scaling for retro look
    this.canvas.style.imageRendering = 'pixelated';
    this.canvas.style.imageRendering = '-moz-crisp-edges';
    this.canvas.style.imageRendering = '-webkit-crisp-edges';

    // Add touch-action for better mobile interaction
    this.canvas.style.touchAction = 'none';
  }

  private async initializeAudio(): Promise<void> {
    if (!this.gameEngine) return;
    
    const audioManager = this.gameEngine.getAudioManager();
    
    // Initialize audio on first user interaction
    const initAudio = async () => {
      try {
        await audioManager.initialize();
        await audioManager.resume();
        console.log('Audio initialized');
        
        // Remove listeners after first interaction
        document.removeEventListener('click', initAudio);
        document.removeEventListener('keydown', initAudio);
        document.removeEventListener('touchstart', initAudio);
      } catch (error) {
        console.warn('Failed to initialize audio:', error);
      }
    };
    
    // Add listeners for user interaction
    document.addEventListener('click', initAudio);
    document.addEventListener('keydown', initAudio);
    document.addEventListener('touchstart', initAudio);
  }

  private registerScenes(): void {
    if (!this.gameEngine) return;
    
    const sceneManager = this.gameEngine.getSceneManager();
    const renderManager = this.gameEngine.getRenderManager();
    const audioManager = this.gameEngine.getAudioManager();
    
    // Create scene switching callbacks
    const switchScene = (scene: SceneType) => {
      this.gameEngine!.switchScene(scene);
    };
    
    const startGame = (gameId: string) => {
      if (this.gameSceneManager) {
        this.gameSceneManager.startGame(gameId);
        this.gameEngine!.switchScene(SceneType.GAME);
      }
    };
    
    // Register main menu scene
    const mainMenuScene = new MainMenuScene(renderManager, audioManager, {
      switchScene,
      startGame
    });
    sceneManager.registerScene(SceneType.MENU, mainMenuScene);
    
    // Register game scene manager
    this.gameSceneManager = new GameSceneManager(renderManager, audioManager, {
      switchScene
    });
    sceneManager.registerScene(SceneType.GAME, this.gameSceneManager);
    
    // Register leaderboard scene
    const leaderboardScene = new LeaderboardScene(renderManager, audioManager, {
      switchScene
    });
    sceneManager.registerScene(SceneType.LEADERBOARD, leaderboardScene);
  }

  private hideLoadingScreen(): void {
    const loadingElements = document.querySelectorAll('.loading');
    loadingElements.forEach(element => {
      element.classList.add('hidden');
    });
  }

  private setupOrientationCheck(): void {
    // Game orientation preferences (games that play better in landscape)
    const landscapeGames = ['defender', 'tunnel-blaster', 'breakout'];
    const portraitGames = ['galactic-defense', 'centipede', 'river-crossing', 'block-drop', 'maze-runner'];

    const checkOrientation = () => {
      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

      if (!isMobile || !this.currentGameId || !this.orientationOverlay) {
        if (this.orientationOverlay) {
          this.orientationOverlay.classList.remove('show');
        }
        return;
      }

      const isLandscape = window.innerWidth > window.innerHeight;
      const isPortrait = window.innerHeight > window.innerWidth;

      let showOverlay = false;
      let message = '';

      if (landscapeGames.includes(this.currentGameId) && isPortrait) {
        showOverlay = true;
        message = 'This game plays better in landscape mode';
      } else if (portraitGames.includes(this.currentGameId) && isLandscape) {
        showOverlay = true;
        message = 'This game plays better in portrait mode';
      }

      if (showOverlay) {
        const subtextElement = document.getElementById('orientationSubtext');
        if (subtextElement) {
          subtextElement.textContent = message;
        }
        this.orientationOverlay.classList.add('show');
      } else {
        this.orientationOverlay.classList.remove('show');
      }
    };

    // Check on orientation change
    window.addEventListener('orientationchange', () => {
      setTimeout(checkOrientation, 200);
    });

    // Check on resize
    window.addEventListener('resize', checkOrientation);

    // Initial check
    checkOrientation();

    // Expose method to set current game
    (window as any).setCurrentGame = (gameId: string | null) => {
      this.currentGameId = gameId;
      (window as any).currentGame = gameId; // Also set globally for canvas sizing
      this.makeCanvasResponsive(); // Resize canvas for the new game
      checkOrientation();
    };
  }

  public cleanup(): void {
    if (this.gameEngine) {
      this.gameEngine.stop();
      this.gameEngine = null;
    }
  }
}

// Application entry point
const app = new Application();

// Initialize application when DOM is loaded
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    app.init().catch(console.error);
  });
} else {
  app.init().catch(console.error);
}

// Handle page unload
window.addEventListener('beforeunload', () => {
  app.cleanup();
});

// Handle visibility change (pause when tab is hidden)
document.addEventListener('visibilitychange', () => {
  // Game engine handles this internally
});

// Prevent default touch behaviors on mobile
document.addEventListener('touchmove', (e) => {
  e.preventDefault();
}, { passive: false });

document.addEventListener('touchstart', (e) => {
  if (e.target === document.body) {
    e.preventDefault();
  }
}, { passive: false });

// Export for debugging
(window as any).app = app;